import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { CARD_TYPES, cardAssignSchema, cardGenerateSchema } from '@app/shared';
import type { CardType, DigitalCardDto, Paged } from '@app/shared';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { FormDialog } from '../../components/dialogs';
import {
  MoneyInput,
  NumberInput,
  SelectInput,
  TextInput,
  applyServerErrors,
  toOptions,
} from '../../components/form';
import { api } from '../../lib/api';
import { formatCents, formatDateTime } from '../../lib/format';
import { withRequiredMessages } from '../inventory/formUtils';

/** A 16-digit card number in groups of four. */
const grouped = (n: string): string => n.replace(/(\d{4})(?=\d)/g, '$1 ');

interface GenerateForm {
  amountCents: number | null;
  cardType: CardType | '';
  count: number | null;
}
const EMPTY: GenerateForm = { amountCents: null, cardType: '', count: 1 };

function GenerateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { control, handleSubmit, reset, setError } = useForm<GenerateForm>({
    defaultValues: EMPTY,
    resolver: withRequiredMessages<GenerateForm>(
      zodResolver(cardGenerateSchema) as unknown as Resolver<GenerateForm>,
      {
        amountCents: 'Amount is required',
        cardType: 'Select Card Type',
        count: 'No. of Cards is required',
      },
    ),
  });
  useEffect(() => {
    if (open) reset(EMPTY);
  }, [open, reset]);
  const generate = useMutation({
    mutationFn: (input: GenerateForm) =>
      api.post<{ created: number }>('/digital-cards/generate', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['digital-cards'] }),
  });
  const submit = handleSubmit(async (values) => {
    try {
      const result = await generate.mutateAsync(values);
      notify.success(`${result.created} card(s) generated.`);
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  return (
    <FormDialog
      open={open}
      title="Generate Card"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={generate.isPending}
      maxWidth="xs"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        <MoneyInput control={control} name="amountCents" label="Amount" required />
        <SelectInput
          control={control}
          name="cardType"
          label="Card Type"
          required
          emptyLabel="Select"
          options={toOptions(CARD_TYPES)}
        />
        <NumberInput
          control={control}
          name="count"
          label="No. of Cards"
          required
          emptyValue={null}
        />
      </Box>
    </FormDialog>
  );
}

function AssignDialog({ card, onClose }: { card: DigitalCardDto | null; onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { control, handleSubmit, reset, setError } = useForm<{ customerId: string }>({
    defaultValues: { customerId: '' },
    resolver: zodResolver(cardAssignSchema),
  });
  useEffect(() => {
    if (card) reset({ customerId: '' });
  }, [card, reset]);
  const assign = useMutation({
    mutationFn: (input: { customerId: string }) =>
      api.post<DigitalCardDto>(`/digital-cards/${card!.id}/assign`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['digital-cards'] }),
  });
  const submit = handleSubmit(async (values) => {
    try {
      const updated = await assign.mutateAsync(values);
      notify.success(`Card assigned to ${updated.assignedTo?.name ?? 'the customer'}.`);
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  return (
    <FormDialog
      open={Boolean(card)}
      title="Assign Card"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={assign.isPending}
      maxWidth="xs"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        <TextInput
          control={control}
          name="customerId"
          label="Customer ID"
          required
          autoFocus
          helperText={card ? `Card ${grouped(card.cardNumber)}` : undefined}
          slotProps={{ htmlInput: { maxLength: 11, inputMode: 'numeric' } }}
        />
      </Box>
    </FormDialog>
  );
}

/** Digital Card (PC1141): cards with an amount and a balance, generated here and assigned to customers. */
export function DigitalCardsPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [generating, setGenerating] = useState(false);
  const [assigning, setAssigning] = useState<DigitalCardDto | null>(null);
  const cards = useQuery({
    queryKey: ['digital-cards', 'list', page, pageSize],
    queryFn: () => api.get<Paged<DigitalCardDto>>('/digital-cards', { page, pageSize }),
    placeholderData: (prev) => prev,
  });

  const columns: Column<DigitalCardDto>[] = [
    { key: '#', header: '#', width: 50, render: (_c, i) => rowNumber(i, page, pageSize) },
    {
      key: 'cardNumber',
      header: 'Card Number',
      nowrap: true,
      render: (c) => (
        <Box>
          {grouped(c.cardNumber)}
          {c.assignedTo ? (
            <Box sx={{ fontSize: 12 }}>
              Assigned to {c.assignedTo.name} ({c.assignedTo.customerId})
            </Box>
          ) : (
            <Box>
              <Button size="small" onClick={() => setAssigning(c)}>
                Assign Card
              </Button>
            </Box>
          )}
        </Box>
      ),
    },
    { key: 'amount', header: 'Amount', align: 'right', render: (c) => formatCents(c.amountCents) },
    {
      key: 'balance',
      header: 'Balance',
      align: 'right',
      render: (c) => formatCents(c.balanceCents),
    },
    { key: 'cardType', header: 'Card Type', render: (c) => c.cardType },
    { key: 'createdBy', header: 'Created By', render: (c) => c.createdBy?.loginId ?? '' },
    {
      key: 'createdAt',
      header: 'Created Date',
      nowrap: true,
      render: (c) => formatDateTime(c.createdAt),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Digital Card"
        code="PC1141"
        actions={
          <Button variant="contained" onClick={() => setGenerating(true)}>
            Generate Card
          </Button>
        }
      />
      <DataTable
        label="Digital Card"
        columns={columns}
        rows={cards.data?.data}
        rowKey={(c) => c.id}
        loading={cards.isFetching}
        error={cards.error}
        onRetry={() => void cards.refetch()}
        emptyText="No record found"
        page={page}
        pageSize={pageSize}
        total={cards.data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        pageSizeOptions={[10, 20, 50, 100]}
      />
      <GenerateDialog open={generating} onClose={() => setGenerating(false)} />
      <AssignDialog card={assigning} onClose={() => setAssigning(null)} />
    </Box>
  );
}
